/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {createAction} from "helpers/createAction";
import {UserType} from "types/enums/UserType";
import {Reservation} from "types/entities/Reservation";
import ajax from "helpers/ajax";
import {ReservationDriver} from "types/entities/ReservationDriver";
import SessionAction from "store/session/SessionAction";
import {ReservationOfferStatus} from "types/enums/ReservationOfferStatus";
import {ColumnFiltersState} from "@tanstack/react-table";
import {DEFAULT_HALL_ID} from "helpers/config";
import {ReservationNote} from "store/booking/BookingAction";
import {
    BookingLoginReducerPushReservationNote,
    BookingLoginReducerRemoveReservation,
    BookingLoginReducerSetReservationDriverCreditLimits,
} from "store/bookingLogin/BookingLoginReducer";
import SellerAction from "store/seller/SellerAction";
import {SortingState} from "@tanstack/table-core/src/features/Sorting";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {Driver} from "types/entities/Driver";

export interface SetReservationDriverCreditLimitPayload {
    reservation_driver_ids: number[];
    credit_limit: number | null;
}

interface SetReservationDriverCreditLimitsResponse {
    reservation?: Reservation;
    reservationDrivers?: ReservationDriver[];
    reservationDriver?: ReservationDriver;
    reservation_driver_ids?: number[];
    credit_limit?: number | null;
    reason?: string;
}

const normalizeReservationDriverCreditLimit = (value: unknown): number | null | undefined => {
    if (value === undefined) {
        return undefined;
    }

    if (value === null) {
        return null;
    }

    if (typeof value !== "number" || !Number.isFinite(value) || !Number.isInteger(value) || value < 0) {
        return undefined;
    }

    return value;
};

export interface UpdateCustomerFromReservationRequest {
    customer: BookingLoginCustomerInReservation;
    reservation: Reservation;
}

export interface UpdateCustomerFromReservationNewRequest {
    customer: BookingLoginCustomerInReservationNew;
    reservation: Reservation;
}

export interface ReservationInvitationRequest {
    email: string;
    reservation_id: number;
    packages_cart_item_id: number;
}

export interface ReservationAssignDriverToItemRequest {
    driver_id: number;
    packages_cart_item_id: number;
    reservation_id: number;
    person_index: number;
}

export interface ReservationInvitationGuestRequest {
    reservation_driver_guid: string;
    name: string;
    last_name: string;
    nickname: string;
    birthdate: string;
    address: string;
    zip: string;
    town: string;
    country: string;
    email: string;
    phone: string;
}

export interface ReservationImportGuestsFromFileRequest {
    file: FormData;
    reservation_id: number;
}

export interface BookingLoginCustomerInReservation {
    driver_id?: number;
    user_type: UserType | string;
    name: string;
    last_name: string;
    nickname: string;
    birthdate: string;
    email?: string;
    address?: string;
    phone?: string;
    reservation_driver_id?: number;
}

export interface BookingLoginCustomerInReservationNew {
    driver_id?: number;
    user_type: UserType | string;
    name: string;
    last_name: string;
    nickname: string;
    birthdate: string;
    email?: string;
    address?: string;
    phone?: string;
    reservation_driver_id?: number;
    packages_cart_item_id: number | null;
    parent_user_id?: number | null;
}

export interface SetCustomerToReservationRequest {
    customer: BookingLoginCustomerInReservation;
    reservation: Reservation;
}

export interface SetCustomerToReservationNewRequest {
    customer: BookingLoginCustomerInReservationNew;
    reservation: Reservation;
}

export interface ExcelImportResponse {
    importState: {
        importCollection: [];
        failureCollection: [];
        importRows: number;
        totalRows: number;
    };
}

export enum ReservationsTableSort {
    Asc = "asc",
    Desc = "desc",
}

export enum ReservationsTableSortBy {
    // Package = "package",
    // Company = "company",
    // PersonCount = "person_count",
    Date = "date",
    // Email = "email",
    Status = "status",
    // Guid = "guid",
    CreatedAt = "created_at",
    CreatedBy = "created_by",
}

export interface ReservationsTableFilter {
    // year: string;
    // month: string;
    // day?: string;
    // package?: number;
    // company?: string;
    // person_count?: {min?: number; max?: number};
    // date?: string;
    // email?: string;
    // status?: ReservationStatus;
    // guid?: string;
    // created_at?: string;
}

export interface ReservationsTableRequest {
    filter?: ReservationsTableFilter;
    page?: number; // page
    per_page?: number; // per_page
    search?: ColumnFiltersState | [];
    sorting?: SortingState | [];
}

class BookingLoginAction {
    static readonly SET_BOOKING_LOGIN_RESERVATION = "BookingLoginAction.SetReservation";
    static readonly REMOVE_PACKAGES_CART_ITEM_FROM_SELECTED_RESERVATION = "BookingLoginAction.RemovePackagesCartItemFromSelectedReservation";
    static readonly REMOVE_RESERVATION_DRIVER_FROM_SELECTED_RESERVATION = "BookingLoginAction.RemoveReservationDriverFromSelectedReservation";
    static readonly SET_BOOKING_LOGIN_SELECTED_RESERVATION_RESERVATION_DRIVER = "BookingLoginAction.SetSelectedReservationReservationDriver";
    static readonly SET_BOOKING_LOGIN_SELECTED_RESERVATION_PACKAGES_CART_ITEM = "BookingLoginAction.SetSelectedReservationPackagesCartItem";
    static readonly SET_BOOKING_LOGIN_USER = "BookingLoginAction.SetUser";
    static readonly SET_BOOKING_LOGIN_RESERVATIONS = "BookingLoginAction.SetReservations";
    static readonly SET_BOOKING_LOGIN_RESERVATION_DRIVER_CREDIT_LIMITS = "BookingLoginAction.SetReservationDriverCreditLimits";
    static readonly REMOVE_RESERVATION_FROM_LIST = "BookingLoginAction.RemoveReservationFromList";
    static readonly CLEAR_BOOKING_LOGIN = "BookingLoginAction.ClearBookingLogin";
    static readonly PUSH_RESERVATION_NOTE = "BookingLoginAction.PushReservationNote";
    static readonly SET_DRIVER_FAMILY_ACCOUNTS = "BookingLoginAction.SetDriverFamilyAccounts";
    static readonly RESET = "BookingLoginAction.Reset";

    static fetchCheckCustomerEmail = (email: string): ((dispatch, getState) => Promise<any>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    exists: boolean;
                } = await ajax.post(
                    "/user/exists-email",
                    {
                        email: email,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response.exists === true) {
                    console.log("fetchLoginCheck - true");
                    return Promise.resolve(response.exists);
                } else if (response.exists === false) {
                    console.log("fetchLoginCheck - false");
                    return Promise.resolve(response.exists);
                } else {
                    return Promise.reject("error");
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchSetCustomerToReservation = (data: SetCustomerToReservationRequest): ((dispatch, getState) => Promise<Reservation>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;

            try {
                const response: {
                    reservation: Reservation;
                    reason?: string;
                } = await ajax.post(
                    "/online-venue/reservation/guests/" + data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response?.reservation) {
                    dispatch(BookingLoginAction.setReservation(response.reservation));
                    return Promise.resolve(response.reservation);
                } else {
                    return Promise.reject(response.reason);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchUpdateCustomerFromReservation = (data: UpdateCustomerFromReservationRequest): ((dispatch, getState) => Promise<Reservation>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    reservation: Reservation;
                    reason?: string;
                } = await ajax.post(
                    "/online-venue/reservation/guests/" + data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response?.reservation) {
                    dispatch(BookingLoginAction.setReservation(response.reservation));
                    return Promise.resolve(response.reservation);
                } else {
                    return Promise.reject(response.reason);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchSetCustomerToReservationNew = (data: SetCustomerToReservationNewRequest): ((dispatch, getState) => Promise<ReservationDriver>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;

            try {
                const response: {
                    reservationDriver: ReservationDriver;
                    reason?: string;
                } = await ajax.post(
                    "/online-venue/reservation/guests/edit/" + data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response?.reservationDriver) {
                    dispatch(BookingLoginAction.setReservationDriverToSelectedReservation(response.reservationDriver));
                    return Promise.resolve(response.reservationDriver);
                } else {
                    return Promise.reject(response.reason);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchUpdateCustomerFromReservationNew = (data: UpdateCustomerFromReservationNewRequest): ((dispatch, getState) => Promise<ReservationDriver>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    reservationDriver: ReservationDriver;
                    reason?: string;
                } = await ajax.post(
                    "/online-venue/reservation/guests/edit/" + data.reservation.id,
                    {
                        ...data.customer,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (response?.reservationDriver) {
                    dispatch(BookingLoginAction.setReservationDriverToSelectedReservation(response.reservationDriver));
                    return Promise.resolve(response.reservationDriver);
                } else {
                    return Promise.reject(response.reason);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchAddOrEditCartItems = (reservation_id: number, reservation_driver_id: number, packages_cart_item_id: number, type: "add" | "remove") => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    reservation: Reservation;
                    reservationDriver: ReservationDriver;
                } = await ajax.post(
                    "/online-venue/reservation/guests/" + reservation_id + "/cart-items",
                    {
                        type: type,
                        reservation_driver_id: reservation_driver_id,
                        packages_cart_item_id: packages_cart_item_id,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (type == "remove") {
                    dispatch(BookingLoginAction.removePackagesCartItemFromSelectedReservation(packages_cart_item_id));
                }

                if (response.reservation && response.reservationDriver && response.reservationDriver.packagesCartItems && type == "add") {
                    response.reservationDriver.packagesCartItems.map(item => dispatch(BookingLoginAction.setPackagesCartItemToSelectedReservation(item)));
                    // dispatch(BookingLoginAction.setReservation(response.reservation));
                }
                console.log("fetchAddOrEditCartItems", response);
                return Promise.resolve(response);
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationInvitation = (data: ReservationInvitationRequest): ((dispatch, getState) => Promise<any>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    packagesCartItem: PackagesCartItem;
                    reservationDriver: ReservationDriver;
                } = await ajax.post(
                    "/online-venue/reservation/invitation-with-items/" + data.reservation_id,
                    {
                        email: data.email,
                        packages_cart_item_id: data.packages_cart_item_id,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                console.log("fetchReservationInvitation", response);

                if (response?.reservationDriver) {
                    return Promise.resolve(response);
                } else {
                    return Promise.reject(response);
                }
            } catch (error) {
                console.log("fetchReservationInvitation - error", error);
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationAssignDriver = (
        data: ReservationAssignDriverToItemRequest
    ): ((
        dispatch,
        getState
    ) => Promise<{
        reservationDriver: ReservationDriver;
        packagesCartItem: PackagesCartItem;
    }>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    reservationDriver: ReservationDriver;
                    packagesCartItem: PackagesCartItem;
                } = await ajax.post(
                    "/online-venue/reservation/assign-to-items/" + data.reservation_id,
                    {
                        driver_id: data.driver_id,
                        packages_cart_item_id: data.packages_cart_item_id,
                        person_index: data.person_index,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                console.log("fetchReservationAssignDriver", response);

                console.log(response.reservationDriver);
                if (response.packagesCartItem) {
                    dispatch(BookingLoginAction.setPackagesCartItemToSelectedReservation(response.packagesCartItem));
                }
                if (response?.reservationDriver) {
                    dispatch(BookingLoginAction.setReservationDriverToSelectedReservation(response.reservationDriver));
                    return Promise.resolve(response);
                } else {
                    return Promise.reject(response);
                }
            } catch (error) {
                console.log("fetchReservationAssignDriver - error", error);
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationInvitationGuest = (data: ReservationInvitationGuestRequest): ((dispatch) => Promise<any>) => {
        return async dispatch => {
            try {
                const response: {
                    data: any;
                } = await ajax.post("/online-venue/reservation/guest/" + data.reservation_driver_guid, {
                    ...data,
                });

                console.log("fetchReservationInvitationGuest", response);
                return Promise.resolve(response.data);

                // if (response?.reservation) {
                //     dispatch(BookingLoginAction.setReservation(response.reservation));
                //     return Promise.resolve(response.reservation);
                // } else {
                //     return Promise.reject(response.reason);
                // }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static getReservationInvitationGuest = (reservation_driver_guid: string): ((dispatch) => Promise<ReservationDriver>) => {
        return async dispatch => {
            try {
                const response: {
                    reservationDriver: ReservationDriver;
                } = await ajax.get("/online-venue/reservation/guest/" + reservation_driver_guid);

                console.log("fetchReservationInvitationGuest", response);
                return Promise.resolve(response.reservationDriver);
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationImportGuestsFromFile = (data: ReservationImportGuestsFromFileRequest): ((dispatch, getState) => Promise<ExcelImportResponse>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: ExcelImportResponse = await ajax.post("/online-venue/reservation/guest-import/" + data.reservation_id, data.file, {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });

                if (response.importState.failureCollection.length === 0) {
                    console.log("on resolve from fetchReservationImportGuestsFromFile", response);
                    return Promise.resolve(response);
                } else {
                    console.log("on reject from fetchReservationImportGuestsFromFile", response);
                    return Promise.reject(response);
                }
            } catch (error) {
                console.error(error);
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationDetail = (reservation_id: number) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            // return new Promise(async (resolve, reject) => {
            try {
                const response: {
                    reservation: Reservation;
                } = await ajax.get("/online-venue/reservation/detail/" + reservation_id, {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });

                if (response?.reservation) {
                    dispatch(BookingLoginAction.setReservation(response.reservation));
                    return Promise.resolve(response);
                    // return resolve(response.reservation);
                } else {
                    dispatch(BookingLoginAction.clearReservations());
                    return Promise.reject("reservation_not_found");
                    // reject(response);
                }
            } catch (error) {
                return Promise.reject("reservation_error");
                // return reject(error);
            }
            // });
        };
    };

    static resolveReservationForCreditLimits = (state: any, reservationId: number) => {
        const selectedReservation = state.bookingLoginState.selectedReservation;
        if (selectedReservation?.id === reservationId) {
            return selectedReservation;
        }

        return state.bookingLoginState.reservations.find((reservation: Reservation) => reservation.id === reservationId) ?? null;
    };

    static fetchSetReservationDriverCreditLimits = (reservationId: number, effectiveDriversLimits: Record<number, number | null | undefined>) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            const state = getState();
            const targetReservation = BookingLoginAction.resolveReservationForCreditLimits(state, reservationId);

            if (!targetReservation) {
                return Promise.reject("reservation_not_loaded");
            }

            const reservationDrivers = targetReservation.reservationDrivers ?? [];
            if (reservationDrivers.length === 0) {
                return Promise.resolve({
                    reservation: targetReservation,
                    reservationDrivers: [],
                    changed: 0,
                });
            }

            const groupedLimits = new Map<number | null, number[]>();

            Object.entries(effectiveDriversLimits).forEach(([reservationDriverIdString, effectiveLimit]) => {
                const reservationDriverId = Number(reservationDriverIdString);
                if (!Number.isFinite(reservationDriverId)) {
                    return;
                }

                const reservationDriver = reservationDrivers.find(item => item.id === reservationDriverId);
                if (!reservationDriver) {
                    return;
                }

                const normalizedEffectiveLimit = normalizeReservationDriverCreditLimit(effectiveLimit);
                if (normalizedEffectiveLimit === undefined) {
                    return;
                }

                const normalizedPersistedLimit = normalizeReservationDriverCreditLimit(reservationDriver.credit_limit);
                if (normalizedPersistedLimit === undefined) {
                    return;
                }

                if (normalizedEffectiveLimit === normalizedPersistedLimit) {
                    return;
                }

                const existingReservationDriverIds = groupedLimits.get(normalizedEffectiveLimit) ?? [];
                existingReservationDriverIds.push(reservationDriverId);
                groupedLimits.set(normalizedEffectiveLimit, existingReservationDriverIds);
            });

            if (groupedLimits.size === 0) {
                return Promise.resolve({
                    reservation: targetReservation,
                    reservationDrivers: reservationDrivers,
                    changed: 0,
                });
            }

            for (const [creditLimit, reservationDriverIds] of groupedLimits.entries()) {
                if (reservationDriverIds.length === 0) {
                    continue;
                }

                const response: SetReservationDriverCreditLimitsResponse = await ajax.post(
                    "/online-venue/reservation/guests/credit-limit/" + reservationId,
                    {
                        reservation_driver_ids: reservationDriverIds,
                        credit_limit: creditLimit,
                    } as SetReservationDriverCreditLimitPayload,
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                // if (response?.reservation) {
                //     dispatch(BookingLoginAction.setReservation(response.reservation));
                //     continue;
                // }

                const updatedReservationDrivers =
                    response?.reservationDrivers && response.reservationDrivers.length > 0
                        ? response.reservationDrivers
                        : response?.reservationDriver
                          ? [response.reservationDriver]
                          : [];

                if (updatedReservationDrivers.length > 0) {
                    dispatch(
                        BookingLoginAction.setReservationDriverCreditLimits({
                            reservationId,
                            reservationDrivers: updatedReservationDrivers,
                        })
                    );
                }
            }

            return Promise.resolve({
                reservation: BookingLoginAction.resolveReservationForCreditLimits(getState(), reservationId),
                changed: groupedLimits.size,
            });
        };
    };

    static fetchSetReservationOfferStatus = (reservation_id: number, offerStatus: ReservationOfferStatus): ((dispatch) => Promise<any>) => {
        return async dispatch => {
            try {
                const response: {
                    reservation: Reservation;
                } = await ajax.post("/online-venue/reservation/seller/set_reservation_offer/" + reservation_id, {offer: offerStatus});
                dispatch(BookingLoginAction.setReservation(response.reservation));
                return Promise.resolve(response);
            } catch (error) {
                console.error(error);
                return Promise.reject(error);
            }
        };
    };

    static fetchDeleteReservationOffer = (reservation_id: number): ((dispatch) => Promise<any>) => {
        return async dispatch => {
            try {
                const response: {
                    reservation: Reservation;
                } = await ajax.delete("/online-venue/reservation/seller/delete_reservation_offer/" + reservation_id, {});
                dispatch(SellerAction.deleteReservation(response.reservation));
                return Promise.resolve(response);
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    // TODO: v pripade potreby doupravit ked bude spojazdnena
    static fetchSetReservationOfferStatusReason = (
        reservation_id: number,
        offerStatus: ReservationOfferStatus,
        reason: string
    ): ((dispatch) => Promise<any>) => {
        return async dispatch => {
            try {
                const response: {
                    reservation: Reservation;
                } = await ajax.post("/online-venue/reservation/seller/set_reservation_offer_with_reason/" + reservation_id, {
                    offer: offerStatus,
                    reason: reason,
                });
                dispatch(BookingLoginAction.setReservation(response.reservation));
                return Promise.resolve(response);
            } catch (error) {
                console.error(error);
                return Promise.reject(error);
            }
        };
    };

    static fetchSellerReservationDetail = (reservation_id: number) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;

            try {
                const response: {
                    reservation?: Reservation;
                    reason?: string;
                } = await ajax.get("/online-venue/reservation/seller/detail/" + reservation_id, {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });

                if (response?.reservation) {
                    dispatch(SellerAction.setReservation(response.reservation));
                    return Promise.resolve(response);
                } else if (response?.reason) {
                    return Promise.resolve(response);
                } else {
                    dispatch(SellerAction.clearReservations());
                    return Promise.reject(response);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationsList = () => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;

            try {
                const response: {
                    reservations: Reservation[];
                } = await ajax.get("/online-venue/reservations/list", {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });
                if (response?.reservations) {
                    dispatch(BookingLoginAction.setReservations(response.reservations));
                    return Promise.resolve(response.reservations);
                } else {
                    return Promise.reject(response);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationsListForSeller = (seller_id: number) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;

            try {
                const response: {
                    reservations: Reservation[];
                } = await ajax.post(
                    "/online-venue/reservation/seller/list",
                    {
                        seller_id: seller_id,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );
                if (response?.reservations) {
                    dispatch(BookingLoginAction.setReservations(response.reservations));
                    return Promise.resolve(response.reservations);
                } else {
                    return Promise.reject(response);
                }
            } catch (error) {
                return Promise.reject(error);
            }
        };
    };

    static fetchReservationsListForSellerWithPagination = (seller_id: number, data: ReservationsTableRequest) => {
        return (dispatch, getState) => {
            const session = getState().sessionState.session;
            return ajax
                .post(
                    // "/admin/reservations/table/all",
                    "/online-venue/reservation/seller/pagination",
                    {
                        hall_id: DEFAULT_HALL_ID,
                        per_page: data?.per_page,
                        page: data?.page,
                        search: data?.search && data.search,
                        // sort: data?.sort && data.sort,
                        // sort_by: data?.sort_by && data.sort_by,
                        sorting: data?.sorting,
                        seller_id: seller_id,
                    },
                    {
                        withCredentials: true,
                        headers: {
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                )
                .then(
                    onResolve => {
                        return Promise.resolve(onResolve.reservations);
                    },
                    onReject => {
                        console.log("fetch data error");
                        console.log(onReject);
                        return Promise.reject(onReject);
                    }
                )
                .catch(error => {
                    if (error.name === "CanceledError") {
                        console.log("Request aborted:", error.message);
                    } else {
                        console.error(error);
                    }
                    return Promise.reject(error);
                });
        };

        // return (dispatch, getState) => {
        //     const session = getState().sessionState.session;
        //     return ajax
        //         .post(
        //             "/online-venue/reservation/seller/list",
        //             {
        //                 // TODO: odkomentovat ked bude api pripravene
        //                 // hall_id: DEFAULT_HALL_ID,
        //                 // per_page: data?.per_page,
        //                 // page: data?.page,
        //                 // search: data?.search && data.search,
        //                 // sort: data?.sort && data.sort,
        //                 // sort_by: data?.sort_by && data.sort_by,
        //                 seller_id: seller_id,
        //             },
        //             {
        //                 withCredentials: true,
        //                 headers: {
        //                     Authorization: `Bearer ${session.access_token}`,
        //                 },
        //             }
        //         )
        //         .then(
        //             onResolve => {
        //                 // TODO: ked bdue api pripravene odstranit premennu {data: ...}
        //                 return {data: onResolve.reservations};
        //             },
        //             onReject => {
        //                 console.log("fetch data error");
        //                 console.log(onReject);
        //             }
        //         )
        //         .catch(error => {
        //             if (error.name === "CanceledError") {
        //                 console.log("Request aborted:", error.message);
        //             } else {
        //                 console.error(error);
        //             }
        //             return error;
        //         });
        // };
    };

    static fetchReservationsLogout = () => {
        return async dispatch => {
            dispatch(BookingLoginAction.clearReservations());
            return dispatch(SessionAction.logout());
        };
    };

    static fetchRemoveReservationDriverFromReservation = (reservation_driver_id: number) => {
        return async (dispatch, getState) => {
            const session = getState().sessionState.session;
            try {
                const response: {
                    status: boolean;
                } = await ajax.delete("/online-venue/reservation/guests/delete/" + reservation_driver_id, {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });
                if (response?.status) {
                    dispatch(BookingLoginAction.removeReservationDriverFromSelectedReservation(reservation_driver_id));
                    return Promise.resolve(response);
                } else {
                    return Promise.reject(response);
                }
            } catch (e) {
                console.log("fetchRemoveReservationDriverFromReservation", e);
            }
        };
    };

    static clearReservations = () => {
        return createAction(BookingLoginAction.CLEAR_BOOKING_LOGIN, []);
    };

    static setDriverFamilyAccounts = (driver: Driver) => {
        return createAction(BookingLoginAction.SET_DRIVER_FAMILY_ACCOUNTS, driver);
    };

    static setReservationDriverToSelectedReservation = (reservationDriver: ReservationDriver) => {
        return createAction(BookingLoginAction.SET_BOOKING_LOGIN_SELECTED_RESERVATION_RESERVATION_DRIVER, reservationDriver);
    };

    static setPackagesCartItemToSelectedReservation = (packagesCartItem: PackagesCartItem) => {
        return createAction(BookingLoginAction.SET_BOOKING_LOGIN_SELECTED_RESERVATION_PACKAGES_CART_ITEM, packagesCartItem);
    };

    static removeReservationDriverFromSelectedReservation = (reservation_driver_id: number) => {
        return createAction(BookingLoginAction.REMOVE_RESERVATION_DRIVER_FROM_SELECTED_RESERVATION, reservation_driver_id);
    };

    static removePackagesCartItemFromSelectedReservation = (packages_cart_item_id: number) => {
        return createAction(BookingLoginAction.REMOVE_PACKAGES_CART_ITEM_FROM_SELECTED_RESERVATION, packages_cart_item_id);
    };

    static setReservations = (reservations: Reservation[]) => {
        return createAction(BookingLoginAction.SET_BOOKING_LOGIN_RESERVATIONS, reservations);
    };

    static setReservationDriverCreditLimits = (data: {
        reservationId: number;
        reservationDrivers: ReservationDriver[];
    }): BookingLoginReducerSetReservationDriverCreditLimits => {
        return createAction(BookingLoginAction.SET_BOOKING_LOGIN_RESERVATION_DRIVER_CREDIT_LIMITS, data);
    };

    static removeReservationFromList = (reservation: Reservation): BookingLoginReducerRemoveReservation => {
        return createAction(BookingLoginAction.REMOVE_RESERVATION_FROM_LIST, reservation);
    };
    static setUser = (user: Driver) => {
        return createAction(BookingLoginAction.SET_BOOKING_LOGIN_USER, user);
    };

    static setReservation = (reservation: Reservation) => {
        return createAction(BookingLoginAction.SET_BOOKING_LOGIN_RESERVATION, reservation);
    };

    static pushReservationNote = (note: ReservationNote): BookingLoginReducerPushReservationNote => {
        return createAction(BookingLoginAction.PUSH_RESERVATION_NOTE, note);
    };

    static resetBookingLoginState = () => {
        return createAction(BookingLoginAction.RESET, null);
    };
}

export default BookingLoginAction;
